import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonGroup, Col, Container, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import PageTitle from '../../components/PageTitle';
import {
  authHeaders,
  extractHeadingsFromHtml,
  flattenNotebookTopics,
  guidesFetch
} from './guidesUtils';
import { readOnlyRichHtml } from '../../components/rteCaptions';
import {
  GUIDE_HIGHLIGHT_COLORS,
  applyAnnotationsToRoot,
  applyFindHighlights,
  authorDisplayName,
  clearFindHighlights,
  findAnnotationMark,
  focusFindHit,
  formatAnnotationDateTime,
  rangeToAnnotationAnchor
} from './guidesAnnotations';
import GuideCommentModal from './GuideCommentModal';
import { printGuideDocument } from './printGuideDocument';
import GuideOutlineSidebar from './GuideOutlineSidebar';
import './guides.css';
import '../../components/references/references.css';

export default function GuideReadPage({ kind }) {
  const { id, topicId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notebook, setNotebook] = useState(null);
  const [article, setArticle] = useState(null);
  const [activeTopicId, setActiveTopicId] = useState(topicId ? Number(topicId) : null);

  const contentRef = useRef(null);
  const selectionRangeRef = useRef(null);
  const pendingAnchorRef = useRef(null);
  const selectionMenuRafRef = useRef(0);
  const selectingRef = useRef(false);
  const markRemoveHideTimerRef = useRef(0);
  const annotationsRef = useRef([]);

  const [annotations, setAnnotations] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [selectionMenuPos, setSelectionMenuPos] = useState(null);
  const [markRemovePopover, setMarkRemovePopover] = useState(null); // { annoId, top, left }
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commentModal, setCommentModal] = useState(null); // { mode, annotationId?, anchor?, quote, body }
  const [commentSaving, setCommentSaving] = useState(false);
  const [contentsToggleHost, setContentsToggleHost] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const searchInputRef = useRef(null);
  const searchMarksRef = useRef([]);
  const searchMatchIndexRef = useRef(0);

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  useEffect(() => {
    searchMatchIndexRef.current = searchMatchIndex;
  }, [searchMatchIndex]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (kind === 'article') {
        const data = await guidesFetch(`/articles/${id}`, { headers: authHeaders() });
        setArticle(data);
      } else {
        const data = await guidesFetch(`/notebooks/${id}`, { headers: authHeaders() });
        setNotebook(data);
        const flat = flattenNotebookTopics(data);
        if (!flat.length) {
          toast.info('This notebook has no topics yet');
          if (data.canEdit) navigate(`/guides/notebooks/${id}`, { replace: true });
          else navigate('/guides/discover', { replace: true });
          return;
        }
        const desired = topicId ? Number(topicId) : flat[0].id;
        const exists = flat.some((t) => t.id === desired);
        setActiveTopicId(exists ? desired : flat[0].id);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load');
      navigate('/guides/mine');
    } finally {
      setLoading(false);
    }
  }, [kind, id, topicId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (kind === 'notebook' && topicId) setActiveTopicId(Number(topicId));
  }, [kind, topicId]);

  const flatTopics = useMemo(
    () => (notebook ? flattenNotebookTopics(notebook) : []),
    [notebook]
  );

  const activeTopic = useMemo(
    () => flatTopics.find((t) => t.id === activeTopicId) || flatTopics[0],
    [flatTopics, activeTopicId]
  );

  const contentHtml = kind === 'article'
    ? (article?.contentHtml || '')
    : (activeTopic?.contentHtml || '');

  const { headings, titleHeading, topicHeadingLevel, htmlWithIds } = useMemo(() => {
    const extracted = extractHeadingsFromHtml(contentHtml, {
      isolateTitle: true,
      fallbackTitle: kind === 'article' ? (article?.title || '') : (activeTopic?.title || '')
    });
    return {
      headings: extracted.headings.filter((heading) => !heading.empty),
      titleHeading: extracted.titleHeading,
      topicHeadingLevel: extracted.topicHeadingLevel,
      htmlWithIds: readOnlyRichHtml(extracted.htmlWithIds)
    };
  }, [contentHtml, kind, article?.title, activeTopic?.title]);

  const docTitle = (!titleHeading || titleHeading.empty)
    ? (kind === 'article' ? (article?.title || '') : (activeTopic?.title || ''))
    : titleHeading.text;

  const annotationTarget = useMemo(() => {
    if (kind === 'article' && article?.id) {
      return { targetType: 'article', targetId: Number(article.id) };
    }
    if (kind === 'notebook' && activeTopic?.id) {
      return { targetType: 'topic', targetId: Number(activeTopic.id) };
    }
    return null;
  }, [kind, article?.id, activeTopic?.id]);

  const canAnnotate = kind === 'article'
    ? !!(article?.canAnnotate ?? article?.canEdit)
    : !!(notebook?.canAnnotate ?? notebook?.canEdit);

  const loadAnnotations = useCallback(async () => {
    if (!annotationTarget || !canAnnotate) {
      setAnnotations([]);
      return;
    }
    try {
      const rows = await guidesFetch(
        `/annotations?targetType=${annotationTarget.targetType}&targetId=${annotationTarget.targetId}`,
        { headers: authHeaders() }
      );
      setAnnotations(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      setAnnotations([]);
    }
  }, [annotationTarget, canAnnotate]);

  useEffect(() => {
    loadAnnotations();
  }, [loadAnnotations]);

  const focusComment = useCallback((annoId) => {
    setCommentsOpen(true);
    setActiveCommentId(annoId);
    requestAnimationFrame(() => {
      const mark = findAnnotationMark(contentRef.current, annoId);
      if (mark) {
        mark.classList.add('guides-anno-flash');
        mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => mark.classList.remove('guides-anno-flash'), 1200);
      }
      const card = document.querySelector(`.guides-comment-card[data-anno-id="${annoId}"]`);
      card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, []);

  const runFind = useCallback((query, preferredIndex = 0) => {
    const root = contentRef.current;
    if (!root) {
      searchMarksRef.current = [];
      setSearchMatchCount(0);
      setSearchMatchIndex(0);
      return;
    }
    const marks = applyFindHighlights(root, query);
    searchMarksRef.current = marks;
    setSearchMatchCount(marks.length);
    if (!marks.length) {
      setSearchMatchIndex(0);
      return;
    }
    const next = focusFindHit(marks, preferredIndex);
    setSearchMatchIndex(next);
  }, []);

  const applyMarks = useCallback(() => {
    const root = contentRef.current;
    if (!root) return;
    clearFindHighlights(root);
    applyAnnotationsToRoot(root, canAnnotate ? annotations : [], {
      onGutterClick: canAnnotate ? focusComment : undefined
    });
    if (searchOpen && searchQuery.trim()) {
      runFind(searchQuery, searchMatchIndexRef.current);
    } else {
      searchMarksRef.current = [];
    }
  }, [annotations, canAnnotate, focusComment, searchOpen, searchQuery, runFind]);

  useEffect(() => {
    const id = window.setTimeout(applyMarks, 0);
    return () => window.clearTimeout(id);
  }, [applyMarks, htmlWithIds]);

  useEffect(() => {
    if (!searchOpen) {
      clearFindHighlights(contentRef.current);
      searchMarksRef.current = [];
      setSearchMatchCount(0);
      setSearchMatchIndex(0);
      return;
    }
    const id = window.setTimeout(() => {
      searchInputRef.current?.focus();
      runFind(searchQuery, 0);
    }, 0);
    return () => window.clearTimeout(id);
    // Only when opening search panel or query typing — applyMarks also re-runs find after anno refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const id = window.setTimeout(() => runFind(searchQuery, 0), 0);
    return () => window.clearTimeout(id);
  }, [searchQuery, searchOpen, runFind]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    clearFindHighlights(contentRef.current);
    searchMarksRef.current = [];
    setSearchMatchCount(0);
    setSearchMatchIndex(0);
  }, []);

  const goSearchMatch = (delta) => {
    const marks = searchMarksRef.current;
    if (!marks.length) return;
    const next = focusFindHit(marks, searchMatchIndex + delta);
    setSearchMatchIndex(next);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      const isFind = (e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === 'f';
      if (!isFind) return;
      const root = contentRef.current;
      if (!root) return;
      // Prefer in-article find while reading
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const hideSelectionMenu = useCallback(() => {
    setSelectionMenuPos(null);
    selectionRangeRef.current = null;
  }, []);

  const stashSelection = useCallback(() => {
    const root = contentRef.current;
    if (!root || typeof window === 'undefined') return null;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return null;
    if (!String(range.toString() || '').trim()) return null;
    selectionRangeRef.current = range.cloneRange();
    return selectionRangeRef.current;
  }, []);

  const refreshSelectionMenu = useCallback(() => {
    if (selectingRef.current) return;
    const range = stashSelection();
    if (!range) {
      hideSelectionMenu();
      return;
    }
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      hideSelectionMenu();
      return;
    }
    setSelectionMenuPos({
      top: rect.top,
      left: rect.left + rect.width / 2
    });
  }, [hideSelectionMenu, stashSelection]);

  const scheduleSelectionMenuRefresh = useCallback(() => {
    if (selectionMenuRafRef.current) cancelAnimationFrame(selectionMenuRafRef.current);
    selectionMenuRafRef.current = requestAnimationFrame(() => {
      selectionMenuRafRef.current = 0;
      refreshSelectionMenu();
    });
  }, [refreshSelectionMenu]);

  useEffect(() => {
    if (!canAnnotate) {
      hideSelectionMenu();
      setMarkRemovePopover(null);
      setCommentsOpen(false);
      return undefined;
    }
    const handleSelectionChange = () => {
      if (commentModal) return;
      scheduleSelectionMenuRefresh();
    };
    const handlePointerDown = (e) => {
      if (
        e.target?.closest?.('.rte-selection-popover')
        || e.target?.closest?.('.guides-anno-remove-popover')
        || e.target?.closest?.('.guides-anno-gutter')
      ) {
        return;
      }
      selectingRef.current = true;
      hideSelectionMenu();
      setMarkRemovePopover(null);
    };
    const handlePointerUp = () => {
      selectingRef.current = false;
      scheduleSelectionMenuRefresh();
    };
    const handleScrollOrResize = () => {
      if (selectionMenuPos) scheduleSelectionMenuRefresh();
      setMarkRemovePopover(null);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      if (selectionMenuRafRef.current) cancelAnimationFrame(selectionMenuRafRef.current);
    };
  }, [canAnnotate, commentModal, hideSelectionMenu, scheduleSelectionMenuRefresh, selectionMenuPos]);

  const restoreSelection = useCallback(() => {
    const range = selectionRangeRef.current;
    if (!range || typeof window === 'undefined') return false;
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }, []);

  const createAnnotation = useCallback(async (payload) => {
    if (!annotationTarget) return null;
    const created = await guidesFetch('/annotations', {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        ...annotationTarget,
        ...payload
      })
    });
    setAnnotations((prev) => [...prev, created].sort((a, b) => a.startOffset - b.startOffset || a.id - b.id));
    return created;
  }, [annotationTarget]);

  const updateAnnotation = useCallback(async (annoId, payload) => {
    const updated = await guidesFetch(`/annotations/${annoId}`, {
      method: 'PATCH',
      headers: authHeaders(true),
      body: JSON.stringify(payload)
    });
    setAnnotations((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    return updated;
  }, []);

  const deleteAnnotation = useCallback(async (annoId) => {
    await guidesFetch(`/annotations/${annoId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    setAnnotations((prev) => prev.filter((a) => a.id !== annoId));
    if (activeCommentId === annoId) setActiveCommentId(null);
  }, [activeCommentId]);

  const showMarkRemovePopover = useCallback((mark, annoId) => {
    if (markRemoveHideTimerRef.current) {
      window.clearTimeout(markRemoveHideTimerRef.current);
      markRemoveHideTimerRef.current = 0;
    }
    const rect = mark.getBoundingClientRect();
    setMarkRemovePopover({
      annoId,
      top: rect.top,
      left: rect.left + rect.width / 2
    });
  }, []);

  const scheduleHideMarkRemovePopover = useCallback(() => {
    if (markRemoveHideTimerRef.current) window.clearTimeout(markRemoveHideTimerRef.current);
    markRemoveHideTimerRef.current = window.setTimeout(() => {
      markRemoveHideTimerRef.current = 0;
      setMarkRemovePopover(null);
    }, 180);
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || loading) return undefined;

    const onPointerOver = (e) => {
      const mark = e.target?.closest?.('mark.guides-anno');
      if (!mark || !root.contains(mark)) return;
      const annoId = Number(mark.dataset.annoId);
      if (!annoId) return;
      const anno = annotationsRef.current.find((a) => a.id === annoId);
      if (!anno?.style) return;
      showMarkRemovePopover(mark, annoId);
    };

    const onPointerOut = (e) => {
      const mark = e.target?.closest?.('mark.guides-anno');
      if (!mark || !root.contains(mark)) return;
      const next = e.relatedTarget;
      if (next?.closest?.('mark.guides-anno') === mark) return;
      if (next?.closest?.('.guides-anno-remove-popover')) return;
      scheduleHideMarkRemovePopover();
    };

    root.addEventListener('pointerover', onPointerOver);
    root.addEventListener('pointerout', onPointerOut);
    return () => {
      root.removeEventListener('pointerover', onPointerOver);
      root.removeEventListener('pointerout', onPointerOut);
      if (markRemoveHideTimerRef.current) window.clearTimeout(markRemoveHideTimerRef.current);
    };
  }, [loading, htmlWithIds, showMarkRemovePopover, scheduleHideMarkRemovePopover]);

  const handleRemoveStyleMark = async (annoId) => {
    const anno = annotationsRef.current.find((a) => a.id === annoId);
    if (!anno) return;
    setMarkRemovePopover(null);
    try {
      if (anno.commentBody) {
        await updateAnnotation(annoId, { style: null, highlightColor: null });
      } else {
        await deleteAnnotation(annoId);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to remove mark');
    }
  };

  const handleChangeHighlightColor = async (annoId, color) => {
    const anno = annotationsRef.current.find((a) => a.id === annoId);
    if (!anno) return;
    if (anno.style === 'highlight' && String(anno.highlightColor || '').toLowerCase() === String(color).toLowerCase()) {
      return;
    }
    try {
      await updateAnnotation(annoId, { style: 'highlight', highlightColor: color });
    } catch (err) {
      toast.error(err.message || 'Failed to change highlight');
    }
  };

  const applyStyleFromMenu = async (style, highlightColor = null) => {
    restoreSelection();
    const root = contentRef.current;
    const range = selectionRangeRef.current;
    if (!root || !range) return;
    const anchor = rangeToAnnotationAnchor(root, range);
    if (!anchor) {
      toast.error('Could not save that selection');
      return;
    }
    hideSelectionMenu();
    window.getSelection()?.removeAllRanges();
    try {
      await createAnnotation({
        ...anchor,
        style,
        highlightColor: style === 'highlight' ? highlightColor : null,
        commentBody: null
      });
    } catch (err) {
      toast.error(err.message || 'Failed to save mark');
    }
  };

  const openCommentFromSelection = () => {
    restoreSelection();
    const root = contentRef.current;
    const range = selectionRangeRef.current;
    if (!root || !range) return;

    const existingMark = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer.closest?.('mark.guides-anno')
      : range.commonAncestorContainer.parentElement?.closest?.('mark.guides-anno');

    if (existingMark?.dataset?.annoId) {
      const annoId = Number(existingMark.dataset.annoId);
      const existing = annotations.find((a) => a.id === annoId);
      if (existing && !existing.commentBody) {
        hideSelectionMenu();
        window.getSelection()?.removeAllRanges();
        setCommentModal({
          mode: 'edit',
          annotationId: existing.id,
          quote: existing.quote,
          body: ''
        });
        return;
      }
    }

    const anchor = rangeToAnnotationAnchor(root, range);
    if (!anchor) {
      toast.error('Could not capture that selection');
      return;
    }
    pendingAnchorRef.current = anchor;
    hideSelectionMenu();
    window.getSelection()?.removeAllRanges();
    setCommentModal({
      mode: 'create',
      anchor,
      quote: anchor.quote,
      body: ''
    });
  };

  const openEditComment = (anno) => {
    setCommentModal({
      mode: 'edit',
      annotationId: anno.id,
      quote: anno.quote,
      body: anno.commentBody || ''
    });
  };

  const handleSaveComment = async (text) => {
    if (!commentModal) return;
    setCommentSaving(true);
    try {
      if (commentModal.mode === 'edit' && commentModal.annotationId) {
        const updated = await updateAnnotation(commentModal.annotationId, { commentBody: text });
        setCommentsOpen(true);
        setActiveCommentId(updated.id);
      } else {
        const anchor = commentModal.anchor || pendingAnchorRef.current;
        if (!anchor) throw new Error('Missing selection');
        const created = await createAnnotation({
          ...anchor,
          style: null,
          highlightColor: null,
          commentBody: text
        });
        setCommentsOpen(true);
        setActiveCommentId(created.id);
      }
      setCommentModal(null);
      pendingAnchorRef.current = null;
    } catch (err) {
      toast.error(err.message || 'Failed to save comment');
    } finally {
      setCommentSaving(false);
    }
  };

  const handleRemoveComment = async (anno) => {
    try {
      if (anno.style) {
        await updateAnnotation(anno.id, { commentBody: null });
      } else {
        await deleteAnnotation(anno.id);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to remove comment');
    }
  };

  const activeIndex = flatTopics.findIndex((t) => t.id === activeTopic?.id);
  const prevTopic = activeIndex > 0 ? flatTopics[activeIndex - 1] : null;
  const nextTopic = activeIndex >= 0 && activeIndex < flatTopics.length - 1
    ? flatTopics[activeIndex + 1]
    : null;

  const goTopic = (tid) => {
    setActiveTopicId(tid);
    navigate(`/guides/notebooks/${id}/read/${tid}`, { replace: true });
  };

  const goSection = (section) => {
    const topics = [...(section.topics || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const first = topics[0];
    if (!first) return;
    goTopic(first.id);
  };

  const scrollToHeading = (headingId) => {
    const el = document.querySelector(`.guides-read-scroll [id="${CSS.escape(headingId)}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const backTarget = () => {
    if (kind === 'article') {
      if (article?.canEdit) return `/guides/articles/${id}/edit`;
      return '/guides/discover';
    }
    return `/guides/notebooks/${id}`;
  };

  const comments = useMemo(
    () => annotations.filter((a) => a.commentBody && String(a.commentBody).trim()),
    [annotations]
  );

  if (loading) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Read" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const title = kind === 'article' ? article?.title : notebook?.title;
  const pageTitle = kind === 'article' ? article?.title : activeTopic?.title;
  const sectionLabel = kind === 'article' ? 'Article' : activeTopic?.sectionTitle;
  const contentColLg = commentsOpen ? 8 : 10;
  const contentColMd = commentsOpen ? 6 : 9;

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={title || 'Read'} />
      <PageTitle title={pageTitle || title || 'Read'} />
      <div className="bg-white border-bottom guides-topic-editor-topbar">
        <Container fluid="lg">
          <div className="d-flex justify-content-between align-items-center py-2 gap-2">
            <div className="d-flex align-items-center gap-3 min-w-0">
              <div ref={setContentsToggleHost} className="d-flex d-md-none" />
              <Button size="sm" variant="light-solid" className="guides-icon-btn d-none d-md-inline-flex" onClick={() => navigate(backTarget())}>
                <i className="bi bi-chevron-left" />
              </Button>
              <div className="min-w-0">
                <h6 className="mb-0 fw-semibold text-truncate">{title}</h6>
                {sectionLabel && (
                  <div className="small mt-0 text-muted text-truncate">{sectionLabel}</div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {kind === 'notebook' && (
                <span className="guides-page-meta text-nowrap">
                  {activeIndex + 1} / {flatTopics.length}
                </span>
              )}
              {searchOpen ? (
                <div className="guides-find-bar">
                  <i className="bi bi-search guides-find-bar-icon" aria-hidden="true" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    className="guides-find-input"
                    placeholder="Find in article…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        closeSearch();
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        goSearchMatch(e.shiftKey ? -1 : 1);
                      }
                    }}
                    aria-label="Find in article"
                  />
                  <span className="guides-find-count text-nowrap">
                    {searchQuery.trim()
                      ? (searchMatchCount ? `${searchMatchIndex + 1} / ${searchMatchCount}` : '0 / 0')
                      : ''}
                  </span>
                  <ButtonGroup>
                    <Button
                      size="sm"
                      variant="light-solid"
                      className="guides-icon-btn px-2 py-1"
                      disabled={!searchMatchCount}
                      onClick={() => goSearchMatch(-1)}
                      title="Previous match"
                      aria-label="Previous match"
                    >
                      <i className="bi bi-chevron-up" />
                    </Button>
                    <Button
                      size="sm"
                      variant="light-solid"
                      className="guides-icon-btn px-2 py-1"
                      disabled={!searchMatchCount}
                      onClick={() => goSearchMatch(1)}
                      title="Next match"
                      aria-label="Next match"
                    >
                      <i className="bi bi-chevron-down" />
                    </Button>
                  </ButtonGroup>
             
                  <Button
                    size="sm"
                    variant="light-lite danger-lite"
                    className="guides-icon-btn px-2 py-1"
                    onClick={closeSearch}
                    title="Close search"
                    aria-label="Close search"
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="light-solid"
                  className="guides-icon-btn"
                  onClick={() => setSearchOpen(true)}
                  title="Find in article"
                  aria-label="Find in article"
                >
                  <i className="bi bi-search" />
                </Button>
              )}
              <Button
                size="sm"
                variant="light-solid"
                className="guides-icon-btn"
                onClick={() => printGuideDocument({ title: pageTitle || title || 'Document' })}
                title="Print"
                aria-label="Print"
              >
                <i className="bi bi-printer" />
              </Button>
              {canAnnotate && (
                <Button
                  size="sm"
                  variant={commentsOpen ? 'primary' : 'light-solid'}
                  className="guides-comments-toggle"
                  onClick={() => setCommentsOpen((v) => !v)}
                  aria-pressed={commentsOpen}
                  title={commentsOpen ? 'Hide comments' : 'Show comments'}
                >
                  <i className="bi bi-chat-left-text" />
                  {comments.length > 0 && (
                    <span className="guides-comments-toggle-count">{comments.length}</span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          <Col lg={2} md={3} className="border-end guides-topic-editor-rail d-none d-md-block">
            <GuideOutlineSidebar
              readOnly
              headings={headings}
              titleFallback={docTitle}
              titleEmpty={!String(docTitle || '').trim()}
              topicHeadingLevel={topicHeadingLevel}
              onScrollTo={scrollToHeading}
              mobileToggleHost={contentsToggleHost}
            >
              {kind === 'notebook' && (
                <div className="guides-outline-notebook">
                  <div className="guides-outline-kicker mb-2">Notebook topics</div>
                  <nav className="guides-read-nav">
                    {[...(notebook?.sections || [])]
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((section) => {
                        const sectionTopics = [...(section.topics || [])]
                          .sort((a, b) => a.sortOrder - b.sortOrder);
                        const isSectionOpen = sectionTopics.some((t) => t.id === activeTopic?.id);
                        return (
                          <div
                            key={section.id}
                            className={`guides-read-section${isSectionOpen ? ' open' : ''}`}
                          >
                            <button
                              type="button"
                              className="guides-read-section-btn"
                              onClick={() => goSection(section)}
                              disabled={sectionTopics.length === 0}
                            >
                              <span className="guides-read-section-btn-label">{section.title}</span>
                              {sectionTopics.length > 0 && (
                                <i
                                  className={`bi ${isSectionOpen ? 'bi-chevron-down' : 'bi-chevron-right'} guides-read-topic-chevron`}
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                            {isSectionOpen && (
                              <ul className="guides-toc">
                                {sectionTopics.map((t) => (
                                  <li
                                    key={t.id}
                                    className={t.id === activeTopic?.id ? 'active' : ''}
                                  >
                                    <button
                                      type="button"
                                      className="guides-read-topic-btn"
                                      onClick={() => goTopic(t.id)}
                                    >
                                      <span className="guides-read-topic-btn-label">{t.title}</span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                  </nav>
                </div>
              )}
            </GuideOutlineSidebar>
          </Col>
          <Col lg={contentColLg} md={contentColMd} className="guides-topic-editor-col border-end">
            <div className="guides-read-panel">
              <div className="guides-read-document">
                <div className="rte-document-body rte-document guides-read-scroll">
                  <div>
                    <article className="guides-read-article rte-document-page eval-rich-content">
                      <div
                        ref={contentRef}
                        className="guides-read-content eval-rich-content rte-editable-root"
                        style={{ color: 'var(--gray-800)' }}
                        dangerouslySetInnerHTML={{ __html: htmlWithIds }}
                      />
                    </article>
                    {kind === 'article' && article?.canEdit && (
                      <div className="pt-3">
                        <Link className="guides-nav-link" to={`/guides/articles/${id}/edit`}>Edit article</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {kind === 'notebook' && (
                <div className="guides-read-footer guides-read-footer-stuck">
                  {prevTopic ? (
                    <button type="button" className="guides-nav-link" onClick={() => goTopic(prevTopic.id)}>
                      <i className="bi bi-chevron-left" /> Previous
                    </button>
                  ) : (
                    <span className="guides-nav-link disabled">Previous</span>
                  )}
                  {nextTopic ? (
                    <button type="button" className="guides-nav-link" onClick={() => goTopic(nextTopic.id)}>
                      Next <i className="bi bi-chevron-right" />
                    </button>
                  ) : (
                    <span className="guides-nav-link disabled">Next</span>
                  )}
                </div>
              )}
            </div>
          </Col>
          {canAnnotate && commentsOpen && (
            <Col lg={2} md={3} className="guides-topic-editor-col guides-comments-sidebar">
              <div className="guides-comments-sidebar-inner">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div className="smaller text-uppercase text-muted fw-semibold">Comments</div>
                  <Button
                    size="sm"
                    variant="light-solid"
                    className="guides-icon-btn"
                    onClick={() => setCommentsOpen(false)}
                    aria-label="Close comments"
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
                {comments.length === 0 ? (
                  <p className="small text-muted mb-0">Select text and add a comment to see it here.</p>
                ) : (
                  <ul className="guides-comments-list">
                    {comments.map((anno) => (
                      <li key={anno.id}>
                        <div
                          className={`guides-comment-card${activeCommentId === anno.id ? ' is-active' : ''}`}
                          data-anno-id={anno.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => focusComment(anno.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              focusComment(anno.id);
                            }
                          }}
                        >
                          <div className="guides-comment-quote-snip">{anno.quote}</div>
                          <div className="guides-comment-body">{anno.commentBody}</div>
                          <div className="guides-comment-hover-meta">
                            <span>{authorDisplayName(anno.author)}</span>
                            <span>{formatAnnotationDateTime(anno.updatedAt || anno.createdAt)}</span>
                          </div>
                          <div className="guides-comment-actions">
                            <button
                              type="button"
                              className="guides-comment-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditComment(anno);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="guides-comment-action"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveComment(anno);
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Col>
          )}
        </Row>
      </Container>

      {canAnnotate && markRemovePopover && (() => {
        const popAnno = annotations.find((a) => a.id === markRemovePopover.annoId)
          || annotationsRef.current.find((a) => a.id === markRemovePopover.annoId);
        const isHighlight = popAnno?.style === 'highlight';
        const activeColor = String(popAnno?.highlightColor || '').toLowerCase();
        return (
          <div
            className="guides-anno-remove-popover shadow-sm"
            role="toolbar"
            aria-label={isHighlight ? 'Highlight options' : 'Remove annotation'}
            style={{ top: markRemovePopover.top, left: markRemovePopover.left }}
            onPointerEnter={() => {
              if (markRemoveHideTimerRef.current) {
                window.clearTimeout(markRemoveHideTimerRef.current);
                markRemoveHideTimerRef.current = 0;
              }
            }}
            onPointerLeave={scheduleHideMarkRemovePopover}
            onMouseDown={(e) => e.preventDefault()}
          >
            {isHighlight && (
              <>
                {GUIDE_HIGHLIGHT_COLORS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`btn btn-sm p-0 rte-highlight-swatch rte-hl-${opt.token}${activeColor === opt.value.toLowerCase() ? ' is-active' : ''}`}
                    title={opt.label}
                    aria-label={`Highlight ${opt.label}`}
                    aria-pressed={activeColor === opt.value.toLowerCase()}
                    onClick={() => handleChangeHighlightColor(markRemovePopover.annoId, opt.value)}
                  />
                ))}
                <span className="rte-selection-popover-sep" />
              </>
            )}
            <Button
              size="sm"
              variant="light-lite danger-lite"
              className="px-2 py-1"
              title="Remove"
              onClick={() => handleRemoveStyleMark(markRemovePopover.annoId)}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </Button>
          </div>
        );
      })()}

      {canAnnotate && selectionMenuPos && (
        <div
          className="rte-selection-popover shadow-sm"
          role="toolbar"
          aria-label="Annotation tools"
          style={{ top: selectionMenuPos.top, left: selectionMenuPos.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Button
            size="sm"
            variant="light-lite"
            className="px-2 py-1"
            title="Underline"
            onClick={() => applyStyleFromMenu('underline')}
          >
            <i className="bi bi-type-underline" />
          </Button>
          <Button
            size="sm"
            variant="light-lite"
            className="px-2 py-1"
            title="Strikethrough"
            onClick={() => applyStyleFromMenu('strike')}
          >
            <i className="bi bi-type-strikethrough" />
          </Button>
          <span className="rte-selection-popover-sep" />
          {GUIDE_HIGHLIGHT_COLORS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`btn btn-sm p-0 rte-highlight-swatch rte-hl-${opt.token}`}
              title={opt.label}
              onClick={() => applyStyleFromMenu('highlight', opt.value)}
            />
          ))}
          <span className="rte-selection-popover-sep" />
          <Button
            size="sm"
            variant="light-lite"
            className="px-2 py-1"
            title="Add comment"
            onClick={openCommentFromSelection}
          >
            <i className="bi bi-chat-left-text" />
          </Button>
        </div>
      )}

      {canAnnotate && (
      <GuideCommentModal
        show={!!commentModal}
        onHide={() => {
          if (!commentSaving) {
            setCommentModal(null);
            pendingAnchorRef.current = null;
          }
        }}
        initialBody={commentModal?.body || ''}
        quote={commentModal?.quote || ''}
        saving={commentSaving}
        onSave={handleSaveComment}
      />
      )}
    </div>
  );
}
